import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../Context";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { GetCodedValueAndAlias, getMapView, highlightFeature } from "../../utils/arcgisUtils";
import { setRightPanelActiveTab } from "../../store/appSlice";
import { RightPanelTabs } from "../../constants/Panels";

import CloseIcon from "../../assets/close.svg?react";
import TrashIcon from "../../assets/trash-can.svg?react";
import { getMorildUserOptions, sendNaticalObjectsToMorild } from "../../utils/nfsUtils";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import Polygon from "@arcgis/core/geometry/Polygon";
import NfsDropdown from "../genericComponents/NfsDropdown/NfsDropdown";
import { StoreState } from "../../store/rootReducer";
import type FeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

const MorildExport = () => {

    const appContext = useContext(AppContext);
    const morildExportSelectedFeatures = appContext?.morildExportSelectedFeatures;
    const mappedHighlightHandlesRef = useRef<{ globalid: string, handle: ResourceHandle }[]>([]);

    const [userOptions, setUserOptions] = useState<{ value: number | string; label: string }[]>([]);
    const [selectedUser, setSelectedUser] = useState<number | null>(null);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const dispatch = useDispatch();

    const [isExporting, setIsExporting] = useState<boolean>(false);

    const getAndSelectFeatures = (polygon: Polygon) => {
        const mapview = getMapView();
        if (!mapview?.map) return;
        // Find layerviews for layers 0, 1, 5, 6
        const layerIdsToCheck = [0, 1, 5, 6];
        const layersToQuery = mapview.allLayerViews.filter(lv => layerIdsToCheck.includes((lv.layer as FeatureLayer).layerId ?? -1) && lv.layer.type === "feature").toArray() as FeatureLayerView[];

        // Query each layer for features within the drawn polygon
        layersToQuery.forEach(layerView => {
            layerView.queryFeatures({
                geometry: polygon,
                spatialRelationship: "intersects"
            }).then((result) => {
                if (result.features.length > 0) {
                    morildExportSelectedFeatures?.addMany(result.features);
                }
            });
        });
        const selectionGraphicsLayer = mapview.map.findLayerById("selectionGraphicsLayer") as GraphicsLayer;
        selectionGraphicsLayer?.removeAll();
    }

    const drawAndGetFeatures = () => {
        const mapview = getMapView();
        if (!mapview?.map) return;
        let selectionGraphicsLayer = mapview.map.findLayerById("selectionGraphicsLayer") as GraphicsLayer;
        if (!selectionGraphicsLayer) {
            selectionGraphicsLayer = new GraphicsLayer({ id: "selectionGraphicsLayer" });
            mapview.map.add(selectionGraphicsLayer);
        }
        selectionGraphicsLayer.removeAll();
        const sketcher = new SketchViewModel({
            view: mapview,
            layer: selectionGraphicsLayer,
            updateOnGraphicClick: false,
        });
        sketcher.create("rectangle");
        sketcher.on("create", (event) => {
            if (event.state === "complete" && event.graphic) {
                getAndSelectFeatures(event.graphic.geometry as Polygon);
            }
        });
    }

    const highlightSelectedFeatures = async () => {
        const mapview = getMapView();
        if (!mapview?.map) return;
        const highlightHandles: { globalid: string, handle: ResourceHandle }[] = [];
        for (const feature of morildExportSelectedFeatures?.value ?? []) {
            // If feature is already highlighted, skip
            const existing = mappedHighlightHandlesRef.current.find(h => h.globalid === feature.attributes.globalid);
            if (existing) {
                highlightHandles.push(existing);
                continue;
            }
            const handle = await highlightFeature(feature);
            if (handle) {
                highlightHandles.push({ globalid: feature.attributes.globalid, handle });
            }
        }
        // Remove highlights that are no longer selected
        mappedHighlightHandlesRef.current.forEach(h => {
            if (!morildExportSelectedFeatures?.value.find(f => f.attributes.globalid === h.globalid)) {
                h.handle.remove();
            }
        });
        mappedHighlightHandlesRef.current = highlightHandles;
    }

    const removeHighlights = () => {
        if (mappedHighlightHandlesRef.current) {
            mappedHighlightHandlesRef.current.forEach(highlight => highlight.handle.remove());
            mappedHighlightHandlesRef.current = [];
        }
    }

    const handleExport = async () => {
        if (!selectedUser) {
            toast.error("Vennligst velg en bruker før eksportering.");
            return;
        }
        const gdbVersion = activeVersion?.versionIdentifier.name;
        setIsExporting(true);
        try {
            const result = await sendNaticalObjectsToMorild(morildExportSelectedFeatures?.value ?? [], selectedUser, gdbVersion);
            if (result) {
                morildExportSelectedFeatures?.clear();
                dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
            } else {
                toast.error("Noe gikk galt under eksportering. Vennligst prøv igjen.");
            }
        } catch (error) {
            console.error("Error exporting objects to Morild:", error);
            toast.error("Noe gikk galt under eksportering. Vennligst prøv igjen.");
        }
        setIsExporting(false);
    };

    const closePanel = () => {
        removeHighlights();
        morildExportSelectedFeatures?.clear();
        dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
    }

    const clearSelection = () => {
        morildExportSelectedFeatures?.clear();
        removeHighlights();
        const mapview = getMapView();
        if (!mapview?.map) return;
        const selectionGraphicsLayer = mapview.map.findLayerById("selectionGraphicsLayer") as GraphicsLayer;
        selectionGraphicsLayer?.removeAll();
    }

    const handleUserChange = (option: string | null) => {
        const selected = userOptions.find(o => o.label === option);
        if (selected) {
            const selectedNumber = parseInt(selected.value as string, 10);
            if (!isNaN(selectedNumber)) {
                setSelectedUser(selectedNumber);
                return;
            }
        } else {
            setSelectedUser(null);
        }
    }

    useEffect(() => {
        if (morildExportSelectedFeatures && morildExportSelectedFeatures?.value.length > 0) {
            toast.info("For å sikre data integritet tømmes valgte objekter ved endring av versjon.", { toastId: 'version-change-morild' });
            morildExportSelectedFeatures?.clear();
            removeHighlights();
        }
    }, [activeVersion]);

    useEffect(() => {
        highlightSelectedFeatures();
    }, [morildExportSelectedFeatures?.value]);

    useEffect(() => {
        getMorildUserOptions().then(options => {
            setUserOptions(options);
        });
    }, [])

    return (
        <div className={`efs-manager-container`}>
            <div className="efs-manager-header w-100 flex-row space-between">
                <h2 className="margin-0">Morild eksport</h2>
                <button type="button" className="empty-button" style={{ position: "absolute", right: "16px", top: "16px" }} onClick={closePanel}>
                    <CloseIcon />
                </button>
            </div>
            <div className="efs-manager-content-container small-gap">
                {morildExportSelectedFeatures?.value.length === 0 &&
                    <span>Velg ett eller flere nautiske objekter for å begynne.</span>
                }
                <span>Du kan velge objekter fra venstre sidepanel eller selektere dem på kartet.</span>
                <div className="flex-row small-gap">
                    <NfsButton onClick={drawAndGetFeatures} className="no-flex fit-content">
                        Velg i kartet
                    </NfsButton>
                    <NfsButton outlined onClick={clearSelection} className="no-flex fit-content warning">
                        Fjern alle valgte
                    </NfsButton>
                </div>
                {morildExportSelectedFeatures?.value && morildExportSelectedFeatures?.value.length > 0 && (
                    <span>Valgte objekter:</span>
                )}

                <div className="efs-manager-content">
                    {morildExportSelectedFeatures?.value && morildExportSelectedFeatures?.value?.length > 0 &&
                        <div className="efs-feature-list flex-col">
                            {morildExportSelectedFeatures?.value?.map((feature) => (
                                <div key={feature.attributes.objectid} className="flex-row small-gap" style={{ borderBottom: "1px solid silver", padding: "0.5rem 1rem" }}>
                                    <span className="flex-row small-gap">
                                        {feature.layer?.title}&nbsp;
                                        {feature.attributes["sjomerke_nr"]}&nbsp;
                                        {feature.attributes.navn}&nbsp;
                                        {GetCodedValueAndAlias(feature.layer, "klasse", feature.attributes["klasse"])[1]}
                                    </span>
                                    <NfsButton
                                        outlined
                                        className="no-flex ml-auto mtb-auto warning fit-content"
                                        onClick={() => {
                                            morildExportSelectedFeatures?.remove(feature);
                                        }}
                                    >
                                        <TrashIcon />
                                    </NfsButton>
                                </div>
                            ))}

                        </div>
                    }
                </div>
            </div>
            <div className="efs-manager-footer">
                <NfsDropdown
                    placeholder="Velg bruker"
                    options={userOptions.map(o => o.label)}
                    selectedOption={selectedUser ? userOptions.find(o => o.value === selectedUser)?.label ?? null : null}
                    setSelectedOption={handleUserChange}
                />
                <NfsButton onClick={handleExport} disabled={isExporting || morildExportSelectedFeatures?.value.length === 0}>
                    {isExporting ? "Eksporterer..." : "Eksporter"}
                </NfsButton>
            </div>

        </div>
    );
};

export default MorildExport;
